import {
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';
import {I18n} from 'aws-amplify/utils';

import * as ZonesService from '../services/ZonesService';
import {classifyWriteError} from '../services/writeErrors';
import {zonesKeys} from './useZonesQuery';
import {
    CreateDnsZoneInput,
    DeleteDnsZoneInput,
    DnsWriteError,
    DnsZone,
} from '../types';
import {writeErrorCopy} from '../writeErrorMessages';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../../components/ServiceNotifications';

// FNP-1191 CRUD half — THE MUTATION HOOKS.
//
// The read half's key factory (`zonesKeys`) was shaped so these could invalidate
// without restructuring the reads, and that is exactly what happens below: the
// comment in useZonesQuery said a create/delete "invalidates zonesKeys.lists()
// on success", and this is that.
//
// ⚠️ THERE IS NO UPDATE HOOK. The neutral schema has no `updateDnsZone` —
// verified against the DEPLOYED TEST API, not merely against the source. See
// services/mutations.ts for why that is a design position rather than a gap.
//
// ⚠️ NOT `notifyError`/`notifySuccess`. The React Query pattern doc's examples
// import those from @flexinet/ui-components, but THIS app has neither — the
// library exports no such symbol and nothing in src/ imports one. The repo's
// own notification helpers are in components/ServiceNotifications, and using
// them is what keeps a zone toast looking like every other toast here.

// The notification text assembled from the class plus the provider's verbatim
// words. Both, always: the class is what an operator scans for, the message is
// what they act on.
const notifyWriteError = (error: DnsWriteError) => {
    const copy = writeErrorCopy(error.kind);
    // ⚠️ The provider's sentence is APPENDED, never substituted for. A
    // re-worded provider message is what made FNP-1087 take a whole
    // investigation.
    const detail = error.providerMessage
        ? `${copy.action} — ${error.providerMessage}`
        : copy.action;
    showErrorNotification(`${copy.title}. ${detail}`);
};

export const useCreateZoneMutation = () => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: CreateDnsZoneInput) => ZonesService.createZone(input),
        onSuccess: (zone: DnsZone | undefined) => {
            // ⚠️ SAYS WHETHER NAMESERVERS ARRIVED, rather than showing a bare
            // count. An empty `nameservers` on a freshly created zone means
            // "not yet assigned" — some providers activate asynchronously —
            // and an operator who sees none with no explanation will
            // reasonably conclude the creation half-failed.
            const assigned = zone?.nameservers?.length ?? 0;
            const detail = assigned > 0
                ? `${I18n.get('Nameservers assigned')}: ${assigned}`
                : I18n.get('Nameservers have not been assigned yet. This provider assigns them asynchronously; reload in a moment.');
            showSuccessNotification(`${I18n.get('Zone created')}: ${zone?.name ?? ''}. ${detail}`);
            queryClient.invalidateQueries({queryKey: zonesKeys.lists()});
        },
        onError: (e: unknown) => {
            notifyWriteError(classifyWriteError(e));
        },
    });
};

export const useDeleteZoneMutation = () => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: DeleteDnsZoneInput) => ZonesService.deleteZone(input),
        onSuccess: (_result, input) => {
            showSuccessNotification(I18n.get('Zone deleted. It has been removed at the provider.'));
            queryClient.invalidateQueries({queryKey: zonesKeys.lists()});
            // The detail query for the deleted zone is now a 404 waiting to
            // happen; removing it beats invalidating it, which would refetch a
            // zone that is gone purely to learn that it is gone.
            queryClient.removeQueries({
                queryKey: zonesKeys.detail(input.connectionId, input.id),
            });
        },
        // ⚠️ THE REFUSAL PATH. `classifyWriteError` recovers ZONE_NOT_EMPTY
        // from the GraphQL envelope and `writeErrorCopy` turns it into "delete
        // the records first" rather than "something went wrong". Consumers also
        // read the classified error off the mutation to render it INLINE in the
        // confirmation modal — see components/DeleteZoneModal — because a toast
        // is gone before an operator has finished reading it.
        onError: (e: unknown) => {
            notifyWriteError(classifyWriteError(e));
        },
    });
};
